import { compositeDisposable, IDisposable } from "../../utils/disposable";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	getSelfOrParentWithClassName,
	hasClass,
	isDescendantOf,
} from "../../common/html";

const selectedClass = "selected";

const getElementToFocus = (el: HTMLElement) => {
	if (!el) return null;
	if (el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
		return el;
	} else {
		const childEl = Array.from(el.childNodes).find(
			(el) => el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement || el instanceof HTMLInputElement,
		);
		if (childEl) return childEl as HTMLElement;
		return expressQuerySelector<HTMLElement>(el, "a, button, input") || el;
	}
};

export interface ICreateDropDownKeyboardNavigatorDependencies {
	onEnter: (e: KeyboardEvent) => void;
	onEscape: (e: KeyboardEvent) => void;
	onArrowUp?: (e: KeyboardEvent) => void;
	onBeforeArrowUp?: () => void;
	onArrowDown?: (e: KeyboardEvent) => void;
	onBeforeArrowDown?: () => void;
	dropdownItemEls?: HTMLElement[];
}

export function createDropDownKeyboardNavigator(
	containerEl: HTMLElement,
	deps: ICreateDropDownKeyboardNavigatorDependencies,
): IDisposable {
	const getActiveElement = (elements: HTMLElement[]) => {
		let focusedEl: HTMLElement;
		const activeEl = document.activeElement as HTMLElement;
		if (activeEl.nodeName === elements[0].nodeName) {
			focusedEl = activeEl as HTMLElement;
		} else if (getSelfOrParentWithClassName(activeEl, "m-navigation-mobile-ui")) {
			focusedEl = getSelfOrParentWithClassName(activeEl, "m-navigation-mobile-ui") as HTMLElement;
		}
		return focusedEl;
	};

	const onArrowDown = (e: KeyboardEvent) => {
		e.preventDefault();
		if (deps.onBeforeArrowDown) {
			closeOtherDropDowns(e);
			deps.onBeforeArrowDown();
		}
		if (!deps.dropdownItemEls || deps.dropdownItemEls.length === 0) return;
		let focusedEl = getActiveElement(deps.dropdownItemEls);
		if (!focusedEl) focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
		let elToFocus = getElementToFocus(deps.dropdownItemEls[0]);
		if (focusedEl) {
			expressRemoveClass(focusedEl, selectedClass);
			const indexOfFocusedEl = Array.prototype.indexOf.call(deps.dropdownItemEls, focusedEl);
			const nextEl = deps.dropdownItemEls[indexOfFocusedEl + 1];
			if (nextEl) {
				elToFocus = getElementToFocus(nextEl);
				selectedClass && expressAddClass(nextEl, selectedClass);
				elToFocus.focus();
			} else {
				selectedClass && expressAddClass(deps.dropdownItemEls[0], selectedClass);
				elToFocus.focus();
			}
		} else {
			selectedClass && expressAddClass(deps.dropdownItemEls[0], selectedClass);
			elToFocus.focus();
		}
	};

	const onArrowUp = (e: KeyboardEvent) => {
		e.preventDefault();
		if (deps.onBeforeArrowUp) deps.onBeforeArrowUp();
		if (!deps.dropdownItemEls || deps.dropdownItemEls.length === 0) return;
		let focusedEl = getActiveElement(deps.dropdownItemEls);
		if (!focusedEl) focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
		let elToFocus = getElementToFocus(deps.dropdownItemEls[deps.dropdownItemEls.length - 1]);
		if (focusedEl) {
			expressRemoveClass(focusedEl, selectedClass);
			const indexOfFocusedEl = Array.prototype.indexOf.call(deps.dropdownItemEls, focusedEl);
			const prevEl = deps.dropdownItemEls[indexOfFocusedEl - 1];
			if (prevEl) {
				elToFocus = getElementToFocus(prevEl);
				selectedClass && expressAddClass(prevEl, selectedClass);
				elToFocus.focus();
			} else {
				selectedClass && expressAddClass(deps.dropdownItemEls[deps.dropdownItemEls.length - 1], selectedClass);
				elToFocus.focus();
			}
		} else {
			selectedClass && expressAddClass(deps.dropdownItemEls[deps.dropdownItemEls.length - 1], selectedClass);
			elToFocus.focus();
		}
	};

	const onEnter = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		const openLanguageEl = hasClass(currentItem, "a-language-selector--open");
		const openDropDownEl = expressQuerySelector(currentItem, ".menu-is-open");
		if (openLanguageEl || openDropDownEl) {
			onEscape(e);
		} else {
			closeOtherDropDowns(e);
			const focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
			if (focusedEl) expressRemoveClass(focusedEl, selectedClass);
			deps.onEnter(e);
		}
	};

	const onEscape = (e: KeyboardEvent) => {
		const focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
		if (focusedEl) expressRemoveClass(focusedEl, selectedClass);
		deps.onEscape(e);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Enter") onEnter(e);
		if (e.key === "Escape") onEscape(e);
		if (e.key === "ArrowUp" && (deps.dropdownItemEls || deps.onArrowUp))
			deps.dropdownItemEls ? onArrowUp(e) : deps.onArrowUp(e);
		if (e.key === "ArrowDown" && (deps.dropdownItemEls || deps.onArrowDown))
			deps.dropdownItemEls ? onArrowDown(e) : deps.onArrowDown(e);
	};

	const closeOtherDropDowns = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		const openDropdowns = expressQuerySelectorAll<HTMLElement>(document, ".menu-is-open, .a-language-selector--open");
		const self =
			getSelfOrParentWithClassName<HTMLElement>(currentItem, "menu-is-open") ||
			getSelfOrParentWithClassName<HTMLElement>(currentItem, "a-language-selector--open");

		const filteredOpenDropdowns = openDropdowns.filter((el) => el !== self && !isDescendantOf(el, currentItem));
		filteredOpenDropdowns.forEach((dropdown) => {
			expressRemoveClass(dropdown, "menu-is-open");
			expressRemoveClass(dropdown, "a-language-selector--open");
		});
	};

	return compositeDisposable([expressEventListener(containerEl, "keydown", onKeyDown)]);
}
